import { cloneElement, isValidElement } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PollyLanguage } from '@mytaverse-shared/interfaces/polly';
import TriggerSection from '../TriggerSection';
import PlaybackPolicySection from '../PlaybackPolicySection';
import InterruptSection from '../InterruptSection';
import AllowSkipToggle from '../AllowSkipToggle';
import DialogueStepsList from '../DialogueStepsList';
import type { QuestionKey } from '../questions';
import type { ScriptDialogState } from '../types';

interface QuestionBodyProps {
  which: QuestionKey;
  state: ScriptDialogState;
  // Languages-view-only: opens the tab strip with no row highlighted (and the
  // steps list hidden behind a placeholder) until the user picks one. Passed
  // by both CreateWizard and EditSummary for their languages step/question.
  languagesForceNoSelection?: boolean;
  onLanguagesTabPick?: () => void;
}

const QuestionBody = ({
  which,
  state,
  languagesForceNoSelection,
  onLanguagesTabPick,
}: QuestionBodyProps) => {
  const { t } = useTranslation('common');
  if (which === 'trigger') {
    return (
      <TriggerSection
        triggerType={state.triggerType}
        title={state.title}
        assistantScript={state.assistantScript}
        mapName={state.mapName}
        activeLanguage={state.activeLanguage}
        effectiveBaseLanguage={state.effectiveBaseLanguage}
        nonBaseLangs={state.nonBaseLangs}
        uiClickButtonLabel={state.uiClickButtonLabel}
        uiClickButtonLabelTranslations={state.uiClickButtonLabelTranslations}
        uiClickButtonLabelStale={state.uiClickButtonLabelStale}
        isTranslating={state.isTranslating}
        onTriggerTypeChange={state.setTriggerType}
        onActiveLanguageChange={state.setActiveLanguage}
        onUiClickButtonLabelChange={state.setUiClickButtonLabel}
        onUiClickButtonLabelTranslationChange={
          state.updateUiClickButtonLabelTranslation
        }
        onTranslateUiClickButtonLabel={state.onTranslateUiClickButtonLabel}
      />
    );
  }

  if (which === 'policy') {
    return (
      <PlaybackPolicySection
        triggerType={state.triggerType}
        playbackPolicy={state.playbackPolicy}
        onPlaybackPolicyChange={state.setPlaybackPolicy}
        skipFirstSession={state.skipFirstSession}
        onSkipFirstSessionChange={state.setSkipFirstSession}
      />
    );
  }

  if (which === 'interrupt') {
    const isButtonDrivenTrigger =
      state.triggerType === 'ui_click' || state.triggerType === 'custom_spot';

    return (
      <>
        {!isButtonDrivenTrigger && (
          <InterruptSection
            interruptMode={state.interruptMode}
            onInterruptModeChange={state.setInterruptMode}
            bridgeRegionGaps={state.bridgeRegionGaps}
            onBridgeRegionGapsChange={state.setBridgeRegionGaps}
          />
        )}
        <AllowSkipToggle
          allowSkipDialog={state.allowSkipDialog}
          onAllowSkipDialogChange={state.setAllowSkipDialog}
        />
      </>
    );
  }

  if (which === 'dialogue') {
    return (
      <DialogueStepsList
        scriptSteps={state.scriptSteps}
        stepExpanded={state.stepExpanded}
        stepUserInputModes={state.stepUserInputModes}
        activeLanguage={state.effectiveBaseLanguage}
        effectiveBaseLanguage={state.effectiveBaseLanguage}
        audioPlaybackKey={state.audioPlaybackKey}
        isAudioPlaying={state.isAudioPlaying}
        userFirstName={state.userFirstName}
        mapName={state.mapName}
        isAddStepFixed={state.isAddStepFixed}
        stepsHeaderRef={state.stepsHeaderRef}
        scriptStepsEndRef={state.scriptStepsEndRef}
        modalTheme={state.modalTheme}
        onToggleExpanded={state.toggleStepExpanded}
        onMoveUp={state.moveStepUp}
        onMoveDown={state.moveStepDown}
        onUpdateStep={state.updateStep}
        onUpdateStepTranslation={state.updateStepTranslation}
        onUserInputModeChange={state.handleUserInputModeChange}
        onRemove={state.removeStep}
        onPlay={state.playAudio}
        onStop={state.stopAudio}
        onAddStep={state.addNewStep}
      />
    );
  }

  // which === 'languages'
  const languageTabs =
    languagesForceNoSelection && isValidElement(state.stepsLanguageTabs)
      ? cloneElement(state.stepsLanguageTabs as React.ReactElement<any>, {
          forceNoSelection: true,
          onActiveLanguageChange: (lang: PollyLanguage) => {
            onLanguagesTabPick?.();
            state.setActiveLanguage(lang);
          },
        })
      : state.stepsLanguageTabs;

  return (
    <>
      {languageTabs}
      {languagesForceNoSelection ? (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ fontSize: 13, color: 'var(--am-text-secondary)' }}>
            {t('assistant.scriptDialog.wizard.selectLanguageToEdit')}
          </Typography>
        </Box>
      ) : (
        <DialogueStepsList
          scriptSteps={state.scriptSteps}
          stepExpanded={state.stepExpanded}
          stepUserInputModes={state.stepUserInputModes}
          activeLanguage={state.activeLanguage}
          effectiveBaseLanguage={state.effectiveBaseLanguage}
          audioPlaybackKey={state.audioPlaybackKey}
          isAudioPlaying={state.isAudioPlaying}
          userFirstName={state.userFirstName}
          mapName={state.mapName}
          isAddStepFixed={state.isAddStepFixed}
          stepsHeaderRef={state.stepsHeaderRef}
          scriptStepsEndRef={state.scriptStepsEndRef}
          modalTheme={state.modalTheme}
          onToggleExpanded={state.toggleStepExpanded}
          onMoveUp={state.moveStepUp}
          onMoveDown={state.moveStepDown}
          onUpdateStep={state.updateStep}
          onUpdateStepTranslation={state.updateStepTranslation}
          onUserInputModeChange={state.handleUserInputModeChange}
          onRemove={state.removeStep}
          onPlay={state.playAudio}
          onStop={state.stopAudio}
          onAddStep={state.addNewStep}
        />
      )}
    </>
  );
};

export default QuestionBody;
